/**
 * Sort comparator for quote item secondary processes, by the organisation's process index.
 * Processes without an index (none expected) go last instead of sorting unpredictably.
 *
 * @param {Object} a - A quote item secondary process, with its `secondaryProcess` details
 * @param {Object} b - Another quote item secondary process
 * @returns {number} Negative when `a` comes first, positive when `b` does
 */
export const compareBySecondaryProcessIndex = (a, b) =>
    (a.secondaryProcess?.index ?? Infinity) - (b.secondaryProcess?.index ?? Infinity)

/**
 * Totals each secondary process across quote items, so a process used on several items shows once.
 * Each item contributes (processing price + setup price) × its quantity.
 *
 * @param {Object[]} [quoteItems] - Quote items with their `secondaryProcesses`
 * @returns {{ secondaryProcessId: string, secondaryProcess: Object, total: number }[]} One entry per process,
 *   in process index order
 */
export const getSecondaryProcessTotals = (quoteItems = []) => {
    const totals = new Map()

    for (const { quantity, secondaryProcesses } of quoteItems) {
        for (const { pricePerItem, secondaryProcess, secondaryProcessId, setupPricePerItem } of secondaryProcesses ??
            []) {
            const lineTotal = ((pricePerItem || 0) + (setupPricePerItem || 0)) * (quantity || 0)
            const current = totals.get(secondaryProcessId)
            totals.set(secondaryProcessId, {
                secondaryProcessId,
                secondaryProcess: current?.secondaryProcess ?? secondaryProcess,
                total: (current?.total ?? 0) + lineTotal,
            })
        }
    }

    return [...totals.values()].sort(compareBySecondaryProcessIndex)
}
